import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import './PatientProspectSignup.css'
import PathwayPicker from './PathwayPicker'
import ConfidentialityTeaser from './ConfidentialityTeaser'
import MobileOtpStep from './MobileOtpStep'
import { PROSPECT_DRAFT_KEY, PROSPECT_DRAFT_TTL_MS, PATHWAYS } from './constants'
import { isLoggedIn } from 'src/consumer/services/patientProspectService'

// Top-level orchestrator for the public Patient-Prospect signup flow at
// /care/get-started.
//
// **Anonymous user:** 3 steps — PathwayPicker → ConfidentialityTeaser →
// MobileOtpStep. On success the mock service stores a session and we
// navigate to /care/status.
//
// **Already-logged-in user** (any prior wellness or other signup):
// the OTP/account-creation steps don't make sense — we don't need a new
// account. Instead, on pathway pick we save the draft and immediately
// route to /care/status, where the user's existing session is used to
// drive the post-login intake flow. If a draft already exists from a
// previous anonymous attempt, we skip even the pathway pick and go
// straight to /care/status. This handles three scenarios cleanly:
//   1. User picked pathway anonymously, signed up via wellness modal,
//      came back → resumes at /care/status.
//   2. User was already logged in, clicked "Get matched with a
//      specialist" CTA → picks pathway → goes to /care/status.
//   3. User picked pathway, refreshed → resumes via draft.

function readDraft() {
  try {
    const raw = window.localStorage.getItem(PROSPECT_DRAFT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > PROSPECT_DRAFT_TTL_MS) {
      window.localStorage.removeItem(PROSPECT_DRAFT_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeDraft(draft) {
  try {
    window.localStorage.setItem(
      PROSPECT_DRAFT_KEY,
      JSON.stringify({ ...draft, savedAt: Date.now() }),
    )
  } catch {
    // ignore
  }
}

function clearDraft() {
  try {
    window.localStorage.removeItem(PROSPECT_DRAFT_KEY)
  } catch {
    // ignore
  }
}

export default function PatientProspectSignup() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const draft = useMemo(readDraft, [])
  const alreadySignedIn = useMemo(isLoggedIn, [])

  const [step, setStep] = useState(draft?.step || 1)
  const [topId, setTopId] = useState(draft?.topId || null)
  const [subId, setSubId] = useState(draft?.subId || null)
  const [resolved, setResolved] = useState(draft?.resolved || null)

  // Already-signed-in user with a complete pathway selection in their
  // draft → skip the whole flow and resume on /care/status. We use replace
  // so the back button doesn't trap them in the picker.
  useEffect(() => {
    if (alreadySignedIn && resolved) {
      navigate('/care/status', { replace: true })
    }
  }, [alreadySignedIn, resolved, navigate])

  // Deep-link support: ?initial=mental-psychiatry etc skips the pathway
  // pick. Useful for marketing CTAs that already know the audience.
  useEffect(() => {
    const init = searchParams.get('initial')
    if (!init || step !== 1 || resolved) return
    const top = PATHWAYS.find((p) => init.startsWith(p.id))
    if (!top) return
    if (top.subOptions) {
      const sub = top.subOptions.find((s) => init === s.id)
      if (!sub) return
      setTopId(top.id)
      setSubId(sub.id)
      setResolved({
        pathway: sub.pathway,
        specialtyTag: sub.specialtyTag,
        topLabel: top.label,
        subLabel: sub.label,
      })
    } else {
      setTopId(top.id)
      setResolved({
        pathway: top.pathway,
        specialtyTag: top.specialtyTag,
        topLabel: top.label,
        subLabel: null,
      })
    }
    // For anonymous users we still need confidentiality + OTP, so step
    // becomes 2. For signed-in users the redirect-to-status effect above
    // takes over instead.
    setStep(2)
  }, [searchParams, step, resolved])

  const handlePathwayPick = (picked) => {
    setTopId(picked.topId)
    setSubId(picked.subId)
    const nextResolved = {
      pathway: picked.pathway,
      specialtyTag: picked.specialtyTag,
      topLabel: picked.topLabel,
      subLabel: picked.subLabel,
    }
    setResolved(nextResolved)

    if (alreadySignedIn) {
      // No need to confirm "create account" or do OTP — they already are
      // signed in. Persist the pick and drop them straight on /care/status,
      // which uses their existing identity.
      writeDraft({
        step: 99, // sentinel: "pathway done; intake continues post-login"
        topId: picked.topId,
        subId: picked.subId,
        resolved: nextResolved,
      })
      navigate('/care/status', { replace: true })
      return
    }

    writeDraft({
      step: 2,
      topId: picked.topId,
      subId: picked.subId,
      resolved: nextResolved,
    })
    setStep(2)
  }

  const handleSignedUp = () => {
    clearDraft()
    navigate('/care/status', { replace: true })
  }

  const goBackTo = (target) => {
    setStep(target)
    writeDraft({ step: target, topId, subId, resolved })
  }

  if (step === 1) {
    return (
      <PathwayPicker
        initialTopId={topId}
        initialSubId={subId}
        onPick={handlePathwayPick}
      />
    )
  }
  if (step === 2) {
    return (
      <ConfidentialityTeaser
        pathwayLabel={resolved?.subLabel || resolved?.topLabel}
        onBack={() => goBackTo(1)}
        onContinue={() => {
          writeDraft({ step: 3, topId, subId, resolved })
          setStep(3)
        }}
      />
    )
  }
  return (
    <MobileOtpStep
      resolvedPathway={resolved}
      onBack={() => goBackTo(2)}
      onSignedUp={handleSignedUp}
    />
  )
}
